import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LOGO_URL, NAV_LINKS, SITE_NAME } from "@/constants/site";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const goSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
      isActive
        ? "text-primary-600 font-medium"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-16 border-b border-background-200/70 bg-background-50/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-foreground-950">
            <img
              src={LOGO_URL}
              alt="熊猫影院 Logo"
              className="h-10 w-10 object-cover"
            />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide text-foreground-950">
              {SITE_NAME}
            </span>
            <span className="mt-0.5 text-[10px] tracking-[0.2em] text-foreground-500">
              PANDA CINEMA
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} className={linkClass} end={link.to === "/"}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={goSearch} className="ml-auto hidden md:block">
          <div className="flex h-10 w-64 items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-3 focus-within:border-primary-400">
            <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              name="q"
              placeholder="搜索电影、电视剧"
              className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-800 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-2-line text-xl"}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="animate-fade-in border-b border-background-200/70 bg-background-50 px-4 py-4 md:hidden">
          <form onSubmit={goSearch} className="mb-3">
            <div className="flex h-11 items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-3">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="q"
                placeholder="搜索电影、电视剧"
                className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-sm cursor-pointer ${
                    isActive
                      ? "bg-primary-100/70 text-primary-700 font-medium"
                      : "text-foreground-800 hover:bg-background-100"
                  }`
                }
              >
                {link.label}
                <i className="ri-arrow-right-s-line text-lg text-foreground-400"></i>
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}